import React, { useCallback, useEffect, useState } from "react";

import { Alert, Badge, Button, Card, Icon, StatePanel, useToast } from "../components/ui";
import { getFileUrl } from "../core/config/api";
import { waBroadcastApi, apiError } from "./waBroadcastApi";
import { clockTime, sendStatus, zoneName } from "./waBroadcastHelpers";
import { useWaAccount } from "./waAccount";
import { usePoll } from "../hooks/usePoll";
import { SendToWhatsAppSheet } from "./SendToWhatsAppSheet";

const POLL_MS = 15000;

/** Recent broadcasts: progress per item, and per group on tap. */
export function WaBroadcastActivity({ refreshKey } = {}) {
  const accountId = useWaAccount();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  // The store's own time zone (Store Settings), or the sending window's.
  const [zone, setZone] = useState(null);
  // A finished broadcast opened in the Send sheet to go out again.
  const [resend, setResend] = useState(null);

  // `rethrow` only from the poll below, so usePoll sees the failure; Retry and
  // row refreshes keep showing it inline as before.
  const load = useCallback(async ({ rethrow = false } = {}) => {
    try {
      const res = await waBroadcastApi.broadcasts(accountId);
      setRows(res.broadcasts);
      setZone(res.timezone || null);
      setError("");
    } catch (err) {
      setError(apiError(err, "Could not load recent broadcasts."));
      if (rethrow) throw err;
    }
  }, [accountId]);

  // usePoll (M4.1): every 15 s while healthy; backs off on errors, stops on
  // 403/404, pauses in a hidden tab.
  const poll = useCallback(() => load({ rethrow: true }), [load]);
  usePoll(poll, { interval: POLL_MS, immediate: true });

  // The page's counts moved (a send went out or was queued): catch up now
  // instead of on the next 15-second tick.
  useEffect(() => {
    if (refreshKey !== undefined) load();
  }, [refreshKey, load]);

  return (
    <Card className="wab-card wab-activity" padding="lg">
      <div className="wab-card__head">
        <h2 className="wab-title rc-wab-heading">Activity</h2>
        {zone && (
          <span className="wab-zone">
            <Icon name="Clock" size="xs" /> {zoneName(zone.timezone)} time
            {zone.source === "store" ? " (your store's time zone)" : " — set your store's time zone in Store Settings"}
          </span>
        )}
      </div>

      {error && <Alert tone="error" title={error} action={{ label: "Retry", onClick: load }} />}

      {rows == null ? (
        !error && <StatePanel state="loading" loadingShape="row" loadingCount={2} compact />
      ) : rows.length === 0 ? (
        <StatePanel
          state="empty"
          compact
          icon="Send"
          title="Nothing sent yet"
          description="Use “Share to Broadcast” on a listing, product page or request. Progress shows up here."
        />
      ) : (
        <ul className="wab-feed">
          {rows.map((row) => (
            <BroadcastRow
              key={row.id}
              row={row}
              tz={zone?.timezone}
              open={openId === row.id}
              onToggle={() => setOpenId(openId === row.id ? null : row.id)}
              onChanged={load}
              onSendAgain={setResend}
            />
          ))}
        </ul>
      )}

      {resend && (
        <SendToWhatsAppSheet
          open
          onClose={() => { setResend(null); load(); }}
          subjectType={resend.subject_type}
          subjectId={resend.subject_id}
          label={resend.label}
          caption={resend.caption}
          imageIds={resend.subject.image_ids}
          noClosingLine={resend.subject.is_order}
          prefill={resend}
        />
      )}
    </Card>
  );
}

function BroadcastRow({ row, tz, open, onToggle, onChanged, onSendAgain }) {
  const accountId = useWaAccount();
  const toast = useToast();
  const [detail, setDetail] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState("");

  const sendAgain = async () => {
    setOpening(true);
    try {
      const res = await waBroadcastApi.broadcast(row.id, accountId);
      if (!res.resend?.subject) {
        toast.error("This item no longer exists", { description: "It was deleted or moved, so it can't be sent again." });
        return;
      }
      onSendAgain(res.resend);
    } catch (err) {
      toast.error(apiError(err, "Could not open this broadcast."));
    } finally {
      setOpening(false);
    }
  };
  const { counts } = row;
  const done = counts.sent + counts.failed + counts.skipped;

  useEffect(() => {
    if (!open) return;
    waBroadcastApi.broadcast(row.id, accountId).then(setDetail).catch((err) => setError(apiError(err, "Could not load the details.")));
  }, [open, row.id, counts.sent, counts.failed, accountId]);

  const cancel = async () => {
    setCancelling(true);
    try {
      const queued = counts.queued;
      setDetail(await waBroadcastApi.cancel(row.id, accountId));
      onChanged();
      toast.success(`Cancelled ${queued} queued message${queued === 1 ? "" : "s"}`, {
        description: "Anything already sent stays sent. Send the item again from the listing if you change your mind.",
      });
    } catch (err) {
      setError(apiError(err, "Could not cancel."));
    } finally {
      setCancelling(false);
    }
  };

  // Purple while it goes out, red when it waits on the number or something failed,
  // green when done. No amber on this page (Alex, 2026-10-23).
  const waitingOnNumber = counts.queued > 0 && row.status !== "cancelled" && !row.finish_eta;
  const status =
    row.status === "cancelled" ? { tone: "muted", label: "Cancelled" }
      : waitingOnNumber ? { tone: "danger", label: "Waiting" }
        : counts.queued > 0 ? { tone: "accent", label: "Sending" }
          : counts.failed > 0 ? { tone: "danger", label: "With errors" }
            : { tone: "success", label: "Finished" };
  const pct = (n) => `${(n / (counts.total || 1)) * 100}%`;
  const to = row.to;
  const more = to && !to.list ? Math.max(0, counts.total - to.groups.length) : 0;

  return (
    <li className={`wab-bcast ${open ? "is-open" : ""}`}>
      {/* The whole row opens the per-group detail on a click; the item name is
          the keyboard/screen-reader toggle. Not one big <button>: "Send again"
          sits inside the row, and a button can't hold another one. */}
      <div className="wab-bcast__summary" onClick={onToggle}>
        {row.image_file_id ? (
          <img className="wab-thumb" src={`${getFileUrl(row.image_file_id)}?height=120`} alt="" loading="lazy" />
        ) : (
          <span className="wab-thumb wab-thumb--empty" aria-hidden="true"><Icon name="Image" size="sm" /></span>
        )}
        <span className="wab-bcast__main">
          <button
            type="button"
            className="wab-row__name wab-bcast__toggle"
            aria-expanded={open}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
          >
            {row.subject_label}
          </button>
          <span className="wab-row__meta">
            {row.subject_type === "request" ? "Request" : "Listing"} · {row.by || "Someone"}
          </span>
          {/* Who it went to: the saved list, or the hand-picked groups (Alex, 2026-10-23). */}
          {to && (to.list || to.groups.length > 0) && (
            <span className="wab-to">
              <span className="wab-to__label">To</span>
              {to.list ? (
                <>
                  <span className="wab-to__tag"><Icon name="List" size="xs" />{to.list}</span>
                  <span className="wab-to__more">{counts.total} groups</span>
                </>
              ) : (
                <>
                  {to.groups.map((g) => <span key={g} className="wab-to__tag">{g}</span>)}
                  {more > 0 && <span className="wab-to__more">+ {more} more</span>}
                </>
              )}
            </span>
          )}
          {/* Clock times, not "x hours ago": started, last out, and when it should be done. */}
          <span className="wab-bcast__times">
            <span>Started {clockTime(row.created_at, tz)}</span>
            {row.last_sent_at && <span>Last sent {clockTime(row.last_sent_at, tz)}</span>}
            {counts.queued > 0 && row.status !== "cancelled" && (
              row.finish_eta
                ? <span className="is-eta">Est. delivery {clockTime(row.finish_eta, tz)}</span>
                : <span className="is-waiting">Waiting for the number</span>
            )}
            {/* Once nothing is left to go out, it can go again — same message,
                photo and groups, all editable first (Alex, 2026-10-16). */}
            {counts.queued === 0 && (
              <Button
                variant="secondary"
                size="sm"
                className="wab-bcast__again"
                loading={opening}
                leadingIcon={<Icon name="RefreshCw" size="sm" />}
                onClick={(e) => { e.stopPropagation(); sendAgain(); }}
              >
                Send again
              </Button>
            )}
          </span>
          <span className="wab-split" role="img" aria-label={`${done} of ${counts.total} handled`}>
            <span className={counts.queued > 0 ? "is-sending" : "is-sent"} style={{ width: pct(counts.sent) }} />
            <span className="is-failed" style={{ width: pct(counts.failed) }} />
          </span>
          <span className="wab-bcast__counts">
            <span className="is-good">{counts.sent} sent</span>
            {counts.skipped > 0 && <span>{counts.skipped} skipped</span>}
            {counts.failed > 0 && <span className="is-bad">{counts.failed} failed</span>}
          </span>
        </span>
        <Badge tone={status.tone} size="sm">{status.label}</Badge>
      </div>

      {open && (
        <div className="wab-bcast__detail">
          {error && <Alert tone="error" title={error} />}
          {!detail ? (
            !error && <StatePanel state="loading" loadingShape="row" compact />
          ) : (
            <ul className="wab-sends">
              {detail.sends.map((s) => {
                const ui = sendStatus(s.status);
                return (
                  <li key={s.id} className="wab-send">
                    <span className="wab-send__group">{s.group || "Removed group"}</span>
                    {s.reason && <span className="wab-send__reason">{s.reason}</span>}
                    <Badge tone={ui.tone} size="sm">{ui.label}</Badge>
                  </li>
                );
              })}
            </ul>
          )}
          {counts.queued > 0 && row.status !== "cancelled" && (
            <Button variant="secondary" size="md" loading={cancelling} leadingIcon={<Icon name="X" size="sm" />} onClick={cancel}>
              Cancel the {counts.queued} still queued
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
